import { create } from "zustand";
import type { Room } from "colyseus.js";
import { isPaletteColor } from "@solblox/shared";

const SKIN_STORAGE_KEY = "solblox.skinColorHex";
const BROADCAST_PUBKEY_STORAGE_KEY = "solblox.broadcastPubkey";

function readBroadcastPubkey(): boolean {
  if (typeof window === "undefined") return true;
  try {
    // Default ON when unset — only an explicit "0" (player opted out in the
    // connect modal) suppresses the attestation.
    return window.localStorage.getItem(BROADCAST_PUBKEY_STORAGE_KEY) !== "0";
  } catch {
    return true;
  }
}

function readPersistedSkin(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(SKIN_STORAGE_KEY);
    if (!raw) return null;
    const lower = raw.toLowerCase();
    return isPaletteColor(lower) ? lower : null;
  } catch {
    return null;
  }
}

/**
 * Local-only view of a player. Mirrors the fields of Player / ArenaPlayer
 * schemas in @solblox/shared but holds plain numbers so React can re-render
 * cheaply without reaching into the Colyseus schema proxy.
 */
export interface PlayerView {
  id: string;
  displayName: string;
  x: number;
  y: number;
  z: number;
  rotY: number;
  avatarUrl: string;
  /** Connected-wallet pubkey (base58) or "" for guests. */
  pubkey: string;
  /** Listen-only spectator: no avatar render, no broadcast position. */
  isSpectator: boolean;
  /** Avatar capsule color. Empty until the server confirms a pick. */
  colorHex: string;
  /** Server-verified token holder — unlocks custom name + holder badge. */
  isHolder: boolean;
}

/**
 * Active broadcast camera shot when the /watch director owns the camera. Null
 * in normal play (no director mounted). Written by BroadcastDirector; read by
 * the broadcast HUD (and, later, the narrator).
 */
export type BroadcastShot =
  | "establish"
  | "dead_air"
  | "pot_spike"
  | "throne_taken"
  | "endgame"
  | "winner";

/** Local-only view of a chat line in the lobby chat log. */
export interface ChatMessageView {
  id: string;
  from: string;
  body: string;
  ts: number;
  /** Sender was a verified token holder at send time. */
  holder: boolean;
}

interface AppStore {
  /** This client's session id, set on lobby join. Empty if not joined. */
  localPlayer: PlayerView | null;
  /** All non-local players as broadcast by the server. */
  remotePlayers: Map<string, PlayerView>;
  /** Lobby chat log, capped to CHAT_HISTORY_CAP on the server side. */
  chatLog: ChatMessageView[];
  /** Server says chat write requires a connected wallet (token economy live). */
  chatGated: boolean;
  /** Currently-connected Colyseus room (lobby OR arena), if any. */
  currentRoom: Room | null;

  setLocalPlayer: (p: PlayerView | null) => void;
  upsertRemotePlayer: (p: PlayerView) => void;
  removeRemotePlayer: (id: string) => void;
  clearRemotePlayers: () => void;
  appendChat: (m: ChatMessageView) => void;
  setChatLog: (log: ChatMessageView[]) => void;
  setChatGated: (v: boolean) => void;
  setCurrentRoom: (r: Room | null) => void;

  /** Locally-chosen skin color (persisted to localStorage). Null = use derived default. */
  skinColorHex: string | null;
  setSkinColorHex: (hex: string | null) => void;

  /**
   * Whether to attest the connected wallet pubkey to the server, so other
   * players, the leaderboard, and the throne can see it. Off = pseudonymous:
   * still playable (touches are on-chain regardless), just not shown to others,
   * and holder perks (badge/custom name) won't apply. Persisted; default on.
   * Toggled by the "View your wallet address" checkbox in the connect modal;
   * read by Lobby when building the pubkey-attest signer.
   */
  broadcastPubkey: boolean;
  setBroadcastPubkey: (v: boolean) => void;

  /** Admin-only cinematic orbit camera for streaming. Off by default. */
  aerialView: boolean;
  setAerialView: (on: boolean) => void;

  /**
   * True while the local player is within touch range of the throne. Written
   * each frame by the player controller (distance to the throne at origin);
   * gates the F-to-touch hotkey + the on-screen "[F] claim" prompt.
   */
  nearThrone: boolean;
  setNearThrone: (v: boolean) => void;

  /** Active /watch broadcast shot, or null when the director isn't running. */
  broadcastShot: BroadcastShot | null;
  setBroadcastShot: (shot: BroadcastShot | null) => void;
}

export const useStore = create<AppStore>((set) => ({
  localPlayer: null,
  remotePlayers: new Map<string, PlayerView>(),
  chatLog: [],
  chatGated: false,
  currentRoom: null,

  setLocalPlayer: (p) => set({ localPlayer: p }),
  upsertRemotePlayer: (p) =>
    set((s) => {
      const next = new Map(s.remotePlayers);
      next.set(p.id, p);
      return { remotePlayers: next };
    }),
  removeRemotePlayer: (id) =>
    set((s) => {
      const next = new Map(s.remotePlayers);
      next.delete(id);
      return { remotePlayers: next };
    }),
  clearRemotePlayers: () => set({ remotePlayers: new Map() }),
  appendChat: (m) =>
    set((s) => {
      // Dedup by id: each room subscription replays existing chat entries via
      // onAdd, so any rejoin (route change, mobile Safari backgrounding,
      // StrictMode double-mount) would otherwise re-append the whole backlog.
      if (s.chatLog.some((x) => x.id === m.id)) return s;
      return { chatLog: [...s.chatLog, m].slice(-200) };
    }),
  setChatLog: (log) => set({ chatLog: log }),
  setChatGated: (v) => set({ chatGated: v }),
  setCurrentRoom: (r) => set({ currentRoom: r }),

  skinColorHex: readPersistedSkin(),
  setSkinColorHex: (hex) => {
    const normalized = hex && isPaletteColor(hex) ? hex.toLowerCase() : null;
    if (typeof window !== "undefined") {
      try {
        if (normalized) window.localStorage.setItem(SKIN_STORAGE_KEY, normalized);
        else window.localStorage.removeItem(SKIN_STORAGE_KEY);
      } catch {
        // localStorage can throw in private mode — silently fall back to memory-only.
      }
    }
    set({ skinColorHex: normalized });
  },

  broadcastPubkey: readBroadcastPubkey(),
  setBroadcastPubkey: (v) => {
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(BROADCAST_PUBKEY_STORAGE_KEY, v ? "1" : "0");
      } catch {
        // localStorage can throw in private mode — fall back to memory-only.
      }
    }
    set({ broadcastPubkey: v });
  },

  aerialView: false,
  setAerialView: (on) => set({ aerialView: on }),

  nearThrone: false,
  setNearThrone: (v) => set({ nearThrone: v }),

  broadcastShot: null,
  setBroadcastShot: (shot) => set({ broadcastShot: shot }),
}));
